import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useRef, useState } from "react";

// A list input that turns each entry into a removable chip. Enter, comma,
// space or leaving the field adds what's typed; Escape clears it; Backspace on an empty field removes the last
// chip; pasting a list adds every entry. `validate` returns an error for an
// entry it refuses, shown under the field.
export function TagInput({
  id,
  values,
  onChange,
  placeholder,
  validate,
  disabled,
}: {
  id?: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  validate?: (value: string) => string | null;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (raw: string) => {
    const entries = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim().toLowerCase().replace(/^@/, ""))
      .filter(Boolean);
    if (!entries.length) return true;
    for (const entry of entries) {
      const problem = validate?.(entry);
      if (problem) {
        setError(problem);
        return false;
      }
    }
    onChange([...new Set([...values, ...entries])]);
    setError(null);
    return true;
  };

  return (
    <div className="space-y-1.5">
      <div
        className={cn(
          "flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 text-sm transition-[border-color,box-shadow]",
          "focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
          error && "border-destructive",
          disabled && "opacity-50",
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-md bg-muted py-0.5 pl-2 pr-1 text-xs font-medium">
            {v}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${v}`}
                className="relative rounded-sm p-0.5 text-muted-foreground hover:bg-background hover:text-foreground after:absolute after:-inset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(values.filter((x) => x !== v));
                }}
              >
                <X className="size-3" />
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          value={draft}
          disabled={disabled}
          placeholder={values.length ? undefined : placeholder}
          className="min-w-[10ch] flex-1 bg-transparent py-0.5 outline-none placeholder:text-muted-foreground"
          onChange={(e) => {
            setError(null);
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            // Tab is left alone (blur adds the entry), so focus can always leave.
            if (["Enter", ",", " "].includes(e.key) && draft.trim()) {
              e.preventDefault();
              if (add(draft)) setDraft("");
            } else if (e.key === "Escape" && draft) {
              e.preventDefault();
              e.stopPropagation();
              setDraft("");
              setError(null);
            } else if (e.key === "Backspace" && !draft && values.length) {
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (draft.trim() && add(draft)) setDraft("");
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[\s,;]/.test(text.trim())) {
              e.preventDefault();
              if (add(text)) setDraft("");
            }
          }}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
